import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, EditIcon, HierarchiesIcon, Modal, Text } from '@viapeople/components'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { queryClient, trpc } from '../../../utils/trpc'
import type { HierarchyFileItem, HierarchySearchInput } from '../../hierarchies/types'
import { CycleManagerCorrectionModal } from './CycleManagerCorrectionModal'
import { ManagerVisibilityModal } from './ManagerVisibilityModal'
import type { ListHierarchiesForCycleInput, ListHierarchiesForCycleOutput, ManagerRow } from './types'

type Hierarchy = ListHierarchiesForCycleOutput['data'][number]

const VISIBILITY_LABELS: Record<ManagerRow['visibility'], string> = {
	default: 'Default',
	skip: 'Skip',
	extended: 'Extended',
}

const columns: Column<Hierarchy>[] = [
	{
		field: 'participantFirstName',
		label: 'Self Evaluator',
		render: (hierarchy) => (
			<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
				{hierarchy.participantFirstName} {hierarchy.participantLastName}
			</Text>
		),
	},
	{
		field: 'participantEmail',
		label: 'Email',
		render: (hierarchy) => (
			<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
				{hierarchy.participantEmail}
			</Text>
		),
	},
	{
		field: 'primaryManagerFirstName',
		label: 'Primary Manager',
		render: (hierarchy) => (
			<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
				{hierarchy.primaryManagerFirstName} {hierarchy.primaryManagerLastName}
			</Text>
		),
	},
	{
		field: 'secondaryManagerFirstName',
		label: 'Secondary Manager',
		render: (hierarchy) => (
			<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
				{hierarchy.secondaryManagerFirstName} {hierarchy.secondaryManagerLastName}
			</Text>
		),
	},
	{
		field: 'coManagers' as keyof Hierarchy,
		label: 'Co-Managers',
		render: (hierarchy) => {
			const coManagers = (
				hierarchy as Hierarchy & {
					coManagers?: { externalId: string; firstName: string | null; lastName: string | null }[]
				}
			).coManagers
			if (!coManagers || coManagers.length === 0) {
				return (
					<Text variant='xsmall' weight='regular' className='text-neutral-400'>
						—
					</Text>
				)
			}
			return (
				<div className='flex flex-wrap gap-1'>
					{coManagers.slice(0, 2).map((cm) => (
						<span
							key={cm.externalId}
							className='inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800'>
							{cm.firstName} {cm.lastName}
						</span>
					))}
					{coManagers.length > 2 && (
						<span className='inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 text-neutral-700'>
							+{coManagers.length - 2}
						</span>
					)}
				</div>
			)
		},
	},
]

const renderBooleanBadge = (value: boolean) => (
	<span
		className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
			value ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'
		}`}>
		{value ? 'Enabled' : 'Disabled'}
	</span>
)

const managerColumns = (onConfigure: (manager: ManagerRow) => void): Column<ManagerRow>[] => [
	{
		field: 'firstName',
		label: 'Manager',
		render: (manager) => (
			<Text variant='xsmall' weight='regular'>
				{manager.firstName} {manager.lastName}
			</Text>
		),
	},
	{
		field: 'email',
		label: 'Email',
		render: (manager) => (
			<Text variant='xsmall' weight='regular'>
				{manager.email}
			</Text>
		),
	},
	{
		field: 'visibility',
		label: 'Visibility',
		className: 'text-center',
		render: (manager) => (
			<span className='inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 text-neutral-700'>
				{VISIBILITY_LABELS[manager.visibility]}
			</span>
		),
	},
	{
		field: 'enableSearchEvaluations',
		label: 'Search Evaluations',
		className: 'text-center',
		render: (manager) => renderBooleanBadge(manager.enableSearchEvaluations),
	},
	{
		field: 'enablePdfViewer',
		label: 'PDF Viewer',
		className: 'text-center',
		render: (manager) => renderBooleanBadge(manager.enablePdfViewer),
	},
	{
		field: 'managerId' as keyof ManagerRow,
		label: '',
		className: 'text-right',
		render: (manager) => (
			<Button variant='secondary' size='small' onClick={() => onConfigure(manager)}>
				Configure
			</Button>
		),
	},
]

export const CycleHierarchyTab = () => {
	const { showToast } = useToast()
	const [isHierarchyModalOpen, setIsHierarchyModalOpen] = useState(false)
	const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false)
	const [isManagerVisibilityModalOpen, setIsManagerVisibilityModalOpen] = useState(false)
	const [selectedManager, setSelectedManager] = useState<ManagerRow | null>(null)
	const [managerSearch, setManagerSearch] = useState('')
	const [selectedManagerIds, setSelectedManagerIds] = useState<Set<string>>(new Set())
	const [bulkVisibility, setBulkVisibility] = useState<ManagerRow['visibility']>('default')
	const [bulkEnableSearch, setBulkEnableSearch] = useState(false)
	const [bulkEnablePdfViewer, setBulkEnablePdfViewer] = useState(true)

	const { cycleId, organizationId } = useParams()

	const managerCols = useMemo(() => managerColumns(setSelectedManager), [])

	const { data: cycleHierarchyFile } = useQuery({
		enabled: !!cycleId && !!organizationId,
		...trpc.admin.cycles.hierarchies.show.queryOptions({
			cycleId: cycleId as string,
			organizationId: organizationId as string,
		}),
	})

	const { data: managers, isLoading: managersLoading } = useQuery({
		enabled: !!cycleId && !!organizationId && !!cycleHierarchyFile?.id,
		...trpc.admin.cycles.hierarchies.managerVisibility.list.queryOptions({
			cycleId: cycleId as string,
			organizationId: organizationId as string,
		}),
	})

	const filteredManagers = useMemo(() => {
		if (!managers) return []
		const q = managerSearch.trim().toLowerCase()
		if (!q) return managers
		return managers.filter(
			(m) =>
				`${m.firstName ?? ''} ${m.lastName ?? ''}`.toLowerCase().includes(q) ||
				(m.email ?? '').toLowerCase().includes(q),
		)
	}, [managers, managerSearch])

	const allFilteredSelected =
		filteredManagers.length > 0 && filteredManagers.every((m) => selectedManagerIds.has(m.managerId))

	const toggleSelectAll = () => {
		if (allFilteredSelected) {
			setSelectedManagerIds((prev) => {
				const next = new Set(prev)
				for (const m of filteredManagers) next.delete(m.managerId)
				return next
			})
		} else {
			setSelectedManagerIds((prev) => {
				const next = new Set(prev)
				for (const m of filteredManagers) next.add(m.managerId)
				return next
			})
		}
	}

	const toggleSelectManager = (id: string) => {
		setSelectedManagerIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const bulkUpsertMutation = useMutation({
		...trpc.admin.cycles.hierarchies.managerVisibility.bulkUpsert.mutationOptions({}),
		onSuccess: () => {
			showToast(`Updated ${selectedManagerIds.size} manager${selectedManagerIds.size === 1 ? '' : 's'}`)
			setSelectedManagerIds(new Set())
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.hierarchies.managerVisibility.list.queryKey({
					cycleId: cycleId as string,
					organizationId: organizationId as string,
				}),
				exact: false,
				refetchType: 'all',
			})
		},
		onError: () => {
			showToast('Failed to update managers', 'error')
		},
	})

	const handleBulkApply = () => {
		bulkUpsertMutation.mutate({
			cycleId: cycleId as string,
			managerIds: Array.from(selectedManagerIds),
			visibility: bulkVisibility,
			enableSearchEvaluations: bulkEnableSearch,
			enablePdfViewer: bulkEnablePdfViewer,
		})
	}

	const invalidateHierarchyQueries = async () => {
		const params = { cycleId: cycleId as string, organizationId: organizationId as string }
		const opts = { exact: false, refetchType: 'all' as const }
		await queryClient.invalidateQueries({ queryKey: trpc.admin.cycles.hierarchies.show.queryKey(params), ...opts })
		await queryClient.invalidateQueries({ queryKey: trpc.admin.cycles.hierarchies.list.queryKey(params), ...opts })
		await queryClient.invalidateQueries({
			queryKey: trpc.admin.cycles.hierarchies.managerVisibility.list.queryKey(params),
			...opts,
		})
	}

	const assignHierarchyFileToCycle = useMutation({
		...trpc.admin.cycles.hierarchies.assign.mutationOptions({}),
		onSuccess: () => {
			showToast('Hierarchy file assigned to cycle')
		},
		onError: () => {
			showToast('An error occurred. Try again later.', 'error')
		},
		onSettled: async () => {
			await invalidateHierarchyQueries()
			setIsHierarchyModalOpen(false)
		},
	})

	const unassignHierarchyMutation = useMutation({
		...trpc.admin.cycles.hierarchies.unassign.mutationOptions({}),
		onSuccess: () => {
			showToast('Reset to organization hierarchy')
		},
		onError: () => {
			showToast('An error occurred. Try again later.', 'error')
		},
		onSettled: async () => {
			await invalidateHierarchyQueries()
		},
	})

	const handleSelectHierarchy = (hierarchyFile: HierarchyFileItem) => {
		assignHierarchyFileToCycle.mutate({
			cycleId: cycleId as string,
			hierarchyFileId: hierarchyFile.id,
			organizationId: organizationId as string,
		})
	}

	const handleResetToOrgHierarchy = () => {
		unassignHierarchyMutation.mutate({
			cycleId: cycleId as string,
			organizationId: organizationId as string,
		})
	}

	const handleClose = () => {
		setIsHierarchyModalOpen(false)
	}

	const isUsingOrgHierarchy = cycleHierarchyFile?.useOrgHierarchy

	return (
		<div className='mt-6 text-left'>
			{cycleHierarchyFile && (
				<div className='ml-2 mb-5'>
					<div className='flex items-center gap-2'>
						<Text variant='small' weight='semibold'>
							Selected Hierarchy:
						</Text>
						<Text
							variant='small'
							weight='semibold'
							className='cursor-pointer text-primary-600'
							onClick={() => setIsHierarchyModalOpen(true)}>
							{cycleHierarchyFile.name}
						</Text>
						{isUsingOrgHierarchy && (
							<span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700'>
								Organization
							</span>
						)}
						<button type='button' className='cursor-pointer' onClick={() => setIsHierarchyModalOpen(true)}>
							<EditIcon className='cursor-pointer' />
						</button>
						<div className='ml-auto flex flex-col items-end gap-1'>
							<div className='flex gap-2'>
								{!isUsingOrgHierarchy && (
									<Button
										variant='secondary'
										size='small'
										onClick={handleResetToOrgHierarchy}
										disabled={unassignHierarchyMutation.isPending}>
										Reset to Organization Hierarchy
									</Button>
								)}
								<Button variant='secondary' size='small' onClick={() => setIsManagerVisibilityModalOpen(true)}>
									Manager Visibility
								</Button>
								<Button variant='secondary' size='small' onClick={() => setIsCorrectionModalOpen(true)}>
									Update Managers
								</Button>
							</div>
							{!isUsingOrgHierarchy && (
								<Text variant='xsmall' className='text-amber-600'>
									Using cycle-specific hierarchy override
								</Text>
							)}
						</div>
					</div>
				</div>
			)}
			<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<Hierarchy, ListHierarchiesForCycleInput>
					columns={columns}
					queryOptionsGenerator={(options) =>
						trpc.admin.cycles.hierarchies.list.queryOptions({
							...options,
							cycleId: cycleId as string,
							organizationId: organizationId as string,
						})
					}
					emptyState={{
						icon: HierarchiesIcon,
						title: 'Select Hierarchy',
						description: 'Get started by selecting a hierarchy',
						actionLabel: 'Select Hierarchy',
						onAction: () => setIsHierarchyModalOpen(true),
					}}
				/>
				<Modal isOpen={isHierarchyModalOpen} onClose={handleClose} title='Select Hierarchy File'>
					{organizationId && <HierarchyModal onSelectHierarchy={handleSelectHierarchy} />}
				</Modal>
			</div>

			<Modal
				isOpen={isManagerVisibilityModalOpen}
				onClose={() => {
					setIsManagerVisibilityModalOpen(false)
					setSelectedManagerIds(new Set())
					setManagerSearch('')
				}}
				className='min-w-[900px]'
				title='Manager Visibility'>
				<div className='flex flex-col gap-4 min-w-[700px]'>
					<Text variant='small' className='text-neutral-500'>
						Configure how much of the hierarchy each manager can see and whether they can search evaluations.
					</Text>

					{/* Search */}
					<input
						type='text'
						placeholder='Search by name or email…'
						value={managerSearch}
						onChange={(e) => setManagerSearch(e.target.value)}
						className='w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-primary-400'
					/>

					{/* Bulk action toolbar */}
					{selectedManagerIds.size > 0 && (
						<div className='flex items-center gap-3 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg'>
							<Text variant='xsmall' weight='semibold' className='text-primary-700 shrink-0'>
								{selectedManagerIds.size} selected
							</Text>
							<div className='flex items-center gap-2 flex-1'>
								<select
									value={bulkVisibility}
									onChange={(e) => setBulkVisibility(e.target.value as ManagerRow['visibility'])}
									className='px-2 py-1.5 text-xs border border-neutral-200 rounded focus:outline-none focus:border-primary-400 bg-white'>
									<option value='default'>Default</option>
									<option value='skip'>Skip-Level</option>
									<option value='extended'>Extended</option>
								</select>
								<label className='flex items-center gap-1.5 cursor-pointer'>
									<input
										type='checkbox'
										checked={bulkEnableSearch}
										onChange={(e) => setBulkEnableSearch(e.target.checked)}
										className='accent-primary-600'
									/>
									<Text variant='xsmall'>Search Evaluations</Text>
								</label>
								<label className='flex items-center gap-1.5 cursor-pointer'>
									<input
										type='checkbox'
										checked={bulkEnablePdfViewer}
										onChange={(e) => setBulkEnablePdfViewer(e.target.checked)}
										className='accent-primary-600'
									/>
									<Text variant='xsmall'>PDF Viewer</Text>
								</label>
							</div>
							<div className='flex items-center gap-2'>
								<Button
									variant='primary'
									size='small'
									onClick={handleBulkApply}
									disabled={bulkUpsertMutation.isPending}>
									{bulkUpsertMutation.isPending ? 'Applying…' : 'Apply to selected'}
								</Button>
								<button
									type='button'
									onClick={() => setSelectedManagerIds(new Set())}
									className='text-xs text-neutral-500 hover:text-neutral-700 underline'>
									Clear
								</button>
							</div>
						</div>
					)}

					<div className='border border-neutral-100 bg-neutral-0 rounded-lg overflow-hidden'>
						{managersLoading ? (
							<div className='p-6 text-center'>
								<Text variant='small' className='text-neutral-500'>
									Loading managers…
								</Text>
							</div>
						) : !managers || managers.length === 0 ? (
							<div className='p-6 text-center'>
								<Text variant='small' className='text-neutral-500'>
									No managers found in the selected hierarchy.
								</Text>
							</div>
						) : filteredManagers.length === 0 ? (
							<div className='p-6 text-center'>
								<Text variant='small' className='text-neutral-500'>
									No managers match your search.
								</Text>
							</div>
						) : (
							<table className='w-full'>
								<thead>
									<tr className='border-b border-neutral-100'>
										<th className='px-4 py-3 w-10'>
											<input
												type='checkbox'
												checked={allFilteredSelected}
												onChange={toggleSelectAll}
												className='accent-primary-600'
											/>
										</th>
										{managerCols.map((col) => (
											<th
												key={String(col.field)}
												className={`px-4 py-3 text-left whitespace-nowrap ${col.className ?? ''}`}>
												<Text variant='xsmall' weight='semibold' className='text-neutral-500'>
													{col.label}
												</Text>
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{filteredManagers.map((manager) => (
										<tr
											key={manager.managerId}
											className={`border-b border-neutral-50 hover:bg-neutral-50 group ${selectedManagerIds.has(manager.managerId) ? 'bg-primary-50' : ''}`}>
											<td className='px-4 py-3 align-middle'>
												<input
													type='checkbox'
													checked={selectedManagerIds.has(manager.managerId)}
													onChange={() => toggleSelectManager(manager.managerId)}
													className='accent-primary-600'
												/>
											</td>
											{managerCols.map((col) => (
												<td key={String(col.field)} className={`px-4 py-3 align-middle ${col.className ?? ''}`}>
													{col.render ? col.render(manager) : String(manager[col.field as keyof ManagerRow] ?? '')}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</Modal>

			<CycleManagerCorrectionModal
				isOpen={isCorrectionModalOpen}
				onClose={() => setIsCorrectionModalOpen(false)}
				cycleId={cycleId as string}
				organizationId={organizationId as string}
			/>

			<ManagerVisibilityModal
				isOpen={!!selectedManager}
				onClose={() => setSelectedManager(null)}
				cycleId={cycleId as string}
				organizationId={organizationId as string}
				manager={selectedManager}
			/>
		</div>
	)
}

const HierarchyModal = ({ onSelectHierarchy }: { onSelectHierarchy: (hierarchy: HierarchyFileItem) => void }) => {
	const { organizationId } = useParams()

	return (
		<div className='w-full'>
			{
				<GenericTable<HierarchyFileItem, HierarchySearchInput>
					columns={[
						{
							field: 'name',
							label: 'Name',
							colSpan: 2,
							render: (hierarchy) => (
								<Text
									variant='xsmall'
									weight='regular'
									className='group-hover:text-primary-600 cursor-pointer'
									onClick={(e) => {
										e.stopPropagation()
										onSelectHierarchy(hierarchy)
									}}>
									{hierarchy.name}
								</Text>
							),
						},
						{
							field: 'createdAt',
							label: 'Created At',
							render: (hierarchy) => {
								const date = new Date(hierarchy.createdAt)
								const month = String(date.getMonth() + 1).padStart(2, '0')
								const day = String(date.getDate()).padStart(2, '0')
								const year = date.getFullYear()
								return (
									<Text variant='xsmall' weight='regular'>
										{`${month}-${day}-${year}`}
									</Text>
								)
							},
						},
					]}
					idField='id'
					showPagination
					onSelectionChange={() => {}}
					queryOptionsGenerator={(options) => ({
						...trpc.admin.hierarchyFiles.search.queryOptions({
							...options,
							organizationId: organizationId as string,
						}),
					})}
					emptyState={{
						title: 'Select your hierarchy',
						description: 'Get started by uploading a hierarchy file',
						actionLabel: 'Select hierarchy',
						icon: HierarchiesIcon,
					}}
				/>
			}
		</div>
	)
}
